import { Helmet } from 'react-helmet-async';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
// @mui
import {
  Card,
  Stack,
  Button,
  Popover,
  MenuItem,
  Typography,
  TextField,
  FormControl,
  Select,
  OutlinedInput,
  Divider,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// components
import Iconify from '../components/iconify';
// sections
import api from 'src/services/BaseService';
import { tokenService } from 'src/services';
import CreateWorkOrderModal from 'src/components/modals/CreateWorkOrderModal';
import {
  isCurrentBranchBlocked,
  requestBlockedDialog,
} from 'src/utils/branchStatus';
// ----------------------------------------------------------------------
import { StatusList } from 'src/types/statusTypes';
import DynamicTable from 'src/components/shared/DynamicTable';
import EditButton from 'src/components/shared/EditButton';
import FilterButton from 'src/components/shared/FilterButton';
import ClearButton from 'src/components/shared/ClearButton';
import ExportToExcelButton from 'src/components/shared/ExportToExcelButton';
import CustomFilter from 'src/components/filter/CustomFilter';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import { useLocalization } from 'src/context/LocalizationContext';
import Services from 'src/services/Services';
import useStateWithCallbackLazy from 'src/hooks/useStateWithCallbackLazy';

function buildPorterAggregatesFromUsedVehicles(usedVehicles = []) {
  const byPorter = {};
  for (const veh of usedVehicles || []) {
    const tool = veh.transportationTool;
    const list = Array.isArray(veh.porters) ? veh.porters : [];
    for (const p of list) {
      const pid = String(p.porterID || '');
      const fullName =
        `${p.name || ''} ${p.lastname || ''}`.trim() || 'Bilinmeyen';
      if (!byPorter[pid]) {
        byPorter[pid] = {
          porterID: pid,
          porterNameLastname: fullName,
          transportationTools: {},
        };
      }
      const add = Number(p.count || 0);
      byPorter[pid].transportationTools[tool] =
        (byPorter[pid].transportationTools[tool] || 0) + add;
    }
  }
  return Object.values(byPorter);
}

// usedVehicles içindeki benzersiz porter sayısı > 1 mi?
function hasMultiplePorters(row) {
  const uvs = row?.usedVehicles || [];
  const seen = new Set();
  for (const v of uvs) {
    for (const p of v.porters || []) {
      const key = String(p.porterID || `${p.name}|${p.lastname}`);
      seen.add(key);
    }
  }
  return seen.size > 1;
}

const calculateAndFormatDeliveryTime = (createdDateTime, completedDateTime) => {
  const createdMoment = moment.unix(createdDateTime);
  const completedMoment = moment.unix(completedDateTime);
  const duration = moment.duration(completedMoment.diff(createdMoment));
  const hours = duration.asHours();

  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} dakika`;
  } else {
    const roundedHours = Math.floor(hours);
    const remainingMinutes = Math.round((hours - roundedHours) * 60);
    if (remainingMinutes > 0) {
      return `${roundedHours} saat ${remainingMinutes} dakika`;
    } else {
      return `${roundedHours} saat`;
    }
  }
};

export default function FinishedJobsPage() {
  const { language } = useLocalization();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const cargoList = ['Tümü', ...vehicles];
  const [filterMultiple, setFilterMultiple] = useState(false);

  const customer = useSelector((state) => state.customerReducer);
  const clearFiltersCalledRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const showCreateModal = location.state?.showCreateModal;

  const [open, setOpen] = useState(null);
  const [orderList, setOrderList] = useState([]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [barcode, setBarcode] = useState('');
  const [selectedCargoStatus, setSelectedCargoStatus] = useState([]);
  const [selectedShipmentType, setSelectedShipmentType] = useState('Tümü');
  const [locations, setLocations] = useState([]);
  const [storeLocationID, setStoreLocationID] = useState();
  const [branches, setBranches] = useState([]);
  const [openCreateModal, setOpenCreateModal] = useState(false);

  useEffect(() => {
    if (showCreateModal) {
      if (isCurrentBranchBlocked()) {
        requestBlockedDialog();
      } else {
        setOpenCreateModal(true);
      }
      // Geçmiş kaydındaki state'i temizle: sayfa yenilendiğinde veya
      // geri/ileri ile bu kayda dönüldüğünde modal tekrar açılmasın.
      navigate(location.pathname + location.search, {
        replace: true,
        state: {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  const [originalOrderList, setOriginalOrderList] = useState([]);
  const [fromTitles, setFromTitles] = useState(['Tümü']);
  const [toTitles, setToTitles] = useState(['Tümü']);
  const [selectedFromTitle, setSelectedFromTitle] = useState('Tümü');
  const [selectedCount, setSelectedCount] = useState('');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('Tümü');
  const [selectedToTitle, setSelectedToTitle] = useState('Tümü');
  const [selectedFromOrToTitle, setSelectedFromOrToTitle] = useState('Tümü');
  const isMobile = useMediaQuery('(max-width:768px)');
  const [showPopUpError, setShowPopUpError] = useState(false);
  const [selectedDocumentNo, setSelectedDocumentNo] = useState('');
  const [selectedHandshakeNo, setSelectedHandshakeNo] = useState('');
  const [selectedShipmentFirm, setSelectedShipmentFirm] = useState('');
  const [selectedCreatorName, setSelectedCreatorName] = useState('');
  const [selectedCreatorType, setSelectedCreatorType] = useState('Tümü');
  const [searchParams, setSearchParams] = useSearchParams();

  const [firstFilterRan, setFirstFilterRan] = useState(false);

  const [filterModal, setFilterModal] = useState(false);

  const initialFilters = {
    startDate: null,
    endDate: null,
    barcode: '',
    selectedCargoStatus: [],
    selectedShipmentType: 'Tümü',
    selectedFromTitle: 'Tümü',
    selectedToTitle: 'Tümü',
    selectedFromOrToTitle: 'Tümü',
    selectedCount: '',
    selectedPerson: '',
    selectedVehicle: 'Tümü',
    selectedDocumentNo: '',
    selectedHandshakeNo: '',
    selectedShipmentFirm: '',
    selectedCreatorType: 'Tümü',
    selectedCreatorName: '',
    filterMultiple: false,
  };

  const [filters, setFilters] = useStateWithCallbackLazy({
    startDate: null,
    endDate: null,
    barcode: '',
    selectedCargoStatus: [],
    selectedShipmentType: 'Tümü',
    selectedFromTitle: 'Tümü',
    selectedToTitle: 'Tümü',
    selectedFromOrToTitle: 'Tümü',
    selectedCount: '',
    selectedPerson: '',
    selectedVehicle: 'Tümü',
    selectedDocumentNo: '',
    selectedHandshakeNo: '',
    selectedShipmentFirm: '',
    selectedCreatorType: 'Tümü',
    selectedCreatorName: '',
    filterMultiple: false,
  });

  const filtersRef = useRef({
    startDate,
    endDate,
    barcode,
    selectedCargoStatus,
    selectedShipmentType,
    selectedFromTitle,
    selectedFromOrToTitle,
    selectedToTitle,
    selectedCount,
    selectedPerson,
    selectedVehicle,
    selectedDocumentNo,
    selectedHandshakeNo,
    selectedShipmentFirm,
    selectedCreatorType,
    selectedCreatorName,
    filterMultiple,
  });

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (searchParams.toString()) {
      const newFilters = {
        startDate: searchParams.get('startDate')
          ? moment(searchParams.get('startDate'), 'YYYY-MM-DD')
          : null,
        endDate: searchParams.get('endDate')
          ? moment(searchParams.get('endDate'), 'YYYY-MM-DD')
          : null,
        barcode: searchParams.get('barcode') || '',
        selectedCargoStatus: searchParams.get('cargoStatus')
          ? JSON.parse(searchParams.get('cargoStatus'))
          : [],
        selectedShipmentType: searchParams.get('shipmentType') || 'Tümü',
        selectedFromTitle: searchParams.get('fromTitle') || 'Tümü',
        selectedToTitle: searchParams.get('toTitle') || 'Tümü',
        selectedFromOrToTitle: searchParams.get('fromOrToTitle') || 'Tümü',
        selectedCount: searchParams.get('count') || '',
        selectedPerson: searchParams.get('person') || '',
        selectedVehicle: searchParams.get('vehicle') || 'Tümü',
        selectedDocumentNo: searchParams.get('documentNo') || '',
        selectedHandshakeNo: searchParams.get('handshakeNo') || '',
        selectedShipmentFirm: searchParams.get('shipmentFirm') || '',
        selectedCreatorType: searchParams.get('creatorType') || 'Tümü',
        selectedCreatorName: searchParams.get('creatorName') || '',
        filterMultiple: searchParams.get('filterMultiple') === 'true',
      };

      // Burada filtreleri güncelle ve sadece callback içinde çalıştır
      setFilters(newFilters, () => {
        getFilteredOrderList();
      });
    } else {
      getFilteredOrderList(); // SearchParam boşsa default çalıştır
    }
  }, []);

  useEffect(() => {
    if (
      !firstFilterRan &&
      JSON.stringify(initialFilters) !== JSON.stringify(filters)
    ) {
      getFilteredOrderList();
      setFirstFilterRan(true);
    }
  }, [filters]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const TABLE_HEAD = [
    {
      id: 'createdDateTime',
      label: language.commons.labels.createdDateTime,
      alignRight: false,
      format: (value) => moment.unix(value).format('DD/MM/YYYY'),
    },
    {
      id: 'createdDateTimeFormat',
      label: language.commons.labels.createdDateTimeFormat,
      alignRight: false,
      format: (value, row) => moment.unix(row.createdDateTime).format('HH:mm'),
    },
    {
      id: 'barcode',
      label: language.commons.labels.barcode,
      alignRight: false,
      sx: { width: 95 },
      format: (value, row) =>
        row.apiUserType === 'handsfree' ? row.orderNumber : row.barcode,
    },
    {
      id: 'documentNo',
      label: 'Belge No',
      alignRight: false,
      sx: { width: 95 },
      format: (value, row) => row.documentNo,
    },
    {
      id: 'handshakeID',
      label: 'İrsaliye No',
      alignRight: false,
      sx: { width: 95 },
      format: (value, row) => row?.handshakeID,
    },
    {
      id: 'title',
      label: language.commons.labels.service_title,
      alignRight: false,
      format: (value, row) => {
        let serviceType =
          row.apiUserType === 'mall-logistics'
            ? 'Taşıma'
            : row.apiUserType === 'eren-holding'
            ? 'Barkodlu Taşıma'
            : row.apiUserType === 'getir'
            ? 'Getir'
            : row.apiUserType === 'handsfree'
            ? 'Handsfree'
            : row.apiUserType === 'derimod'
            ? 'Derimod'
            : row.apiUserType === 'ipekyol'
            ? 'İpekyol'
            : row.apiUserType;

        if (
          row.dataJSON.isMultipleTransportation === true ||
          row.dataJSON.isMultipleTransportation === '1'
        ) {
          serviceType += ' (Ç)';
        }

        return serviceType;
      },
    },
    {
      id: 'from',
      label: language.commons.labels.from,
      alignRight: false,
      sortBy: 'from.title',
      format: (value, row) => row.from.title,
    },
    {
      id: 'to',
      label: language.commons.labels.to,
      alignRight: false,
      sortBy: 'to.title',
      format: (value, row) => row.to.title,
    },
    {
      id: 'isReserved',
      label: 'Rezervasyonlu İşlem mi?',
      alignRight: false,
      format: (value, row) => {
        const reservation = row?.dataJSON?.reservationDateTime;
        const hasReservation =
          reservation !== null &&
          reservation !== undefined &&
          String(reservation).trim().length > 0;

        return hasReservation ? 'Evet' : 'Hayır';
      },
    },
    {
      id: 'reservationDate',
      label: 'Rezervasyon Tarihi',
      alignRight: false,
      format: (value, row) => {
        const reservation = row?.dataJSON?.reservationDateTime;
        if (!reservation || String(reservation).trim().length === 0) {
          return '';
        }

        // Örn: "2025-11-25 13:22" → "25.11.2025 13:22:00"
        return moment(reservation).format('DD.MM.YYYY HH:mm');
      },
    },
    {
      id: 'completedDateTime',
      label: language.commons.labels.completedDateTime,
      alignRight: false,
      format: (value, row) =>
        row.completedDateTime ? moment.unix(value).format('DD/MM/YYYY') : '',
    },
    {
      id: 'completedDateTimeFormat',
      label: language.commons.labels.completedDateTimeFormat,
      alignRight: false,
      format: (value, row) =>
        row.completedDateTime
          ? moment.unix(row.completedDateTime).format('HH:mm')
          : '',
    },
    {
      id: 'processTime',
      label: language.commons.labels.processTime,
      alignRight: false,
      format: (value, row) =>
        row.completedDateTime
          ? calculateAndFormatDeliveryTime(
              row.createdDateTime,
              row.completedDateTime
            )
          : '',
    },
    {
      id: 'shipmentFirm',
      label: 'Kargo Firması',
      alignRight: false,
    },
    {
      id: 'createdByInfo',
      label: 'Oluşturan Kişi Adı / Türü',
      alignRight: false,
      format: (value, row) => {
        let type = '';
        if (!row.createdBy) {
          type = 'Belirtilmemiş';
        } else {
          if (row.createdBy === 'corporational') {
            type = 'Mağaza';
          } else if (row.createdBy === 'porter') {
            type = 'Porter';
          } else {
            type = row.createdBy;
          }
        }
        return (
          (row.createdByName ? row.createdByName : '-') +
          ' ' +
          (row.createdByLastname ? row.createdByLastname : '-') +
          ' / ' +
          type.charAt(0).toUpperCase() +
          type.slice(1)
        );
      },
    },
    {
      id: 'porters',
      label: language.commons.labels.porterNameLastname,
      alignRight: false,
      sx: { maxWidth: 95 },
      format: (value, row) => {
        const aggs = buildPorterAggregatesFromUsedVehicles(
          row?.usedVehicles || []
        );
        return aggs.map((p) => p.porterNameLastname).join(', ');
      },
    },
    {
      id: 'orderInventory',
      label: language.commons.labels.orderInventory,
      alignRight: false,
      sx: { maxWidth: 95 },
      format: (value) => Number(value),
    },
    {
      id: 'usedVehicles',
      label: language.commons.labels.usedVehicles,
      sx: { maxWidth: 95 },
      alignRight: false,
      format: (value, row) => {
        const uvs = row?.usedVehicles || [];
        const showReview = uvs.length > 1 || hasMultiplePorters(row);

        if (showReview) {
          return (
            <Button
              onClick={() => {
                // mevcut davranışı koruyarak incele modalini açıyoruz
                setSelectedJob(row);
              }}
              variant="contained"
              sx={{ color: 'white' }}
            >
              {language.commons.review}
            </Button>
          );
        }

        // Tek araç ve tek porter ise eski tekli görünüm
        if (uvs.length === 1) {
          return (
            <>
              <div className="customRow">
                <div style={{ textAlign: 'right' }}>
                  {uvs.map((e, index) => (
                    <div key={index} style={{ textAlign: 'right' }}>
                      {e.transportationTool === 'Ring'
                        ? 'Koli/Paket (Ring)'
                        : e.transportationTool}
                    </div>
                  ))}
                </div>
              </div>
            </>
          );
        }

        // usedVehicles boşsa fallback
        return (
          <>
            <div className="customRow">
              <div>
                <div style={{ textAlign: 'right' }}>
                  {row?.requestedTransportationTool === 'Ring'
                    ? 'Koli/Paket (Ring)'
                    : row?.requestedTransportationTool}
                </div>
              </div>
            </div>
          </>
        );
      },
    },

    {
      id: 'status',
      label: language.commons.labels.status,
      sx: { width: 245 },
      alignCenter: true,
      format: (value, row) => {
        // --- REZERVASYONLU KONTROLÜ ---
        const reservationDateTime = row?.dataJSON?.reservationDateTime;
        const hasFutureReservation =
          reservationDateTime &&
          String(reservationDateTime).trim().length > 0 &&
          moment(reservationDateTime).isAfter(moment());

        if (
          row?.status === '3' &&
          row?.dataJSON?.isReserved === true &&
          hasFutureReservation
        ) {
          return (
            <div
              style={{
                backgroundColor: '#E5EFFF',
                color: '#3D3EB8',
                padding: '4px 7px',
                borderRadius: '5px',
                textAlign: 'center',
                fontWeight: 600,
              }}
            >
              Rezervasyonlu
            </div>
          );
        }
        // ----------------------------------

        const statusLabel = StatusList.filter(
          (item) => item.id.toString() === row.status
        )[0]?.label;

        let backgroundColor = '';
        if (
          ['1', '10', '15', '16', '17', '21', '22', '28'].includes(row.status)
        ) {
          backgroundColor = 'rgba(10, 127, 50, 0.24)';
        } else if (
          ['3', '9', '11', '12', '13', '14', '20', '26'].includes(row.status)
        ) {
          backgroundColor = 'rgba(247, 169, 55, 0.36)';
        } else if (['2', '23', '24', '25', '29'].includes(row.status)) {
          backgroundColor = 'rgba(216, 27, 96, 0.4)';
        } else if (
          ['4', '5', '6', '7', '8', '19', '27', '32'].includes(row.status)
        ) {
          backgroundColor = 'rgb(87, 90, 255, 0.4)';
        }

        return (
          <div
            style={{
              backgroundColor,
              padding: '4px 7px',
              borderRadius: '5px',
              textAlign: 'center',
            }}
          >
            {statusLabel}
          </div>
        );
      },
    },

    {
      id: 'operation',
      label: '',
      alignCenter: true,
      format: (value, row) => (
        <EditButton
          id={'detail_button'}
          tooltip="Gönderi Detay"
          icon={'bx:detail'}
          onClick={() => {
            const currentFilters = filtersRef.current;
            const queryParams = new URLSearchParams({
              startDate: currentFilters.startDate
                ? moment(currentFilters.startDate).format('YYYY-MM-DD')
                : '',
              endDate: currentFilters.endDate
                ? moment(currentFilters.endDate).format('YYYY-MM-DD')
                : '',
              barcode: currentFilters.barcode,
              cargoStatus: JSON.stringify(currentFilters.selectedCargoStatus),
              shipmentType: currentFilters.selectedShipmentType,
              fromTitle: currentFilters.selectedFromTitle,
              toTitle: currentFilters.selectedToTitle,
              fromOrToTitle: currentFilters.selectedFromOrToTitle,
              count: currentFilters.selectedCount,
              person: currentFilters.selectedPerson,
              vehicle: currentFilters.selectedVehicle,
              documentNo: currentFilters.selectedDocumentNo,
              handshakeNo: currentFilters.selectedHandshakeNo,
              shipmentFirm: currentFilters.selectedShipmentFirm,
              creatorType: currentFilters.selectedCreatorType,
              creatorName: currentFilters.selectedCreatorName,
              filterMultiple: currentFilters.filterMultiple,
            }).toString();

            window.open(
              `/dashboard/finished-jobs-detail/${encodeURIComponent(
                row.barcode
              )}?${queryParams}`,
              '_blank'
            );
          }}
        />
      ),
      sx: { width: 245 },
    },
  ];

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 0;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showButton, setShowButton] = useState(true); // Butonun görünürlüğünü kontrol eder

  // const steps = [
  //   {
  //     target: '.my-zero-step',
  //     content: 'Eğitime Hoşgeldiniz ',
  //     disableBeacon: true,
  //   },
  //   {
  //     target: '.my-first-step',
  //     content: 'Bu ilk Adımda İş emri oluşturabilirsiniz',
  //     disableBeacon: true,

  //     hideFooter: true,
  //   },
  //   {
  //     target: '.my-second-step',
  //     content:
  //       'Burada Barkod Numaranızı Girip Aradığınız Barkod numarasına sahip emri görebilirsiniz',
  //     styles: {
  //       options: {
  //         zIndex: 10000,
  //       },
  //     },
  //     title: 'The schedule',
  //   },
  //   {
  //     target: '.my-k-step',
  //     content: 'Burada Seçebilirsiniz ',
  //     disableBeacon: true,
  //     disableOverlayClose: true,
  //     hideCloseButton: true,
  //     hideFooter: true,
  //     spotlightClicks: true,
  //     styles: {
  //       options: {
  //         zIndex: 10000,
  //       },
  //     },
  //   },
  //   {
  //     target: '.my-third-step',
  //     content:
  //       'Burada aramak istediğiniz  İş Emirlerini arayıp filtreleyebilirsiniz ',
  //   },
  // ];
  // const handleJoyrideCallback = (data) => {
  //   const { action, index, status, type } = data;
  //   //console.log("datak",type);

  //   if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
  //     setRun(false);
  //     setStepIndex(0);
  //     setShowButton(true);
  //   } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
  //     const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
  //     if (index === 0) {
  //       setTimeout(() => {
  //         setRun(true);
  //         setStepIndex(nextStepIndex);
  //       }, 400);
  //     } else if (index === 1) {
  //       if (action === ACTIONS.PREV) {
  //         setOpenCreateModal(false);
  //       } else {
  //         setOpenCreateModal(true);
  //       }
  //       setRun(false);
  //       setStepIndex(nextStepIndex);
  //       setTimeout(() => {
  //         setRun(true);
  //       }, 400);
  //     } else if (index === 2) {
  //       setRun(false);
  //       setStepIndex(nextStepIndex);

  //       setTimeout(() => {
  //         setRun(true);
  //       }, 400);
  //     } else if (index === 3) {
  //       setRun(true);
  //       setStepIndex(nextStepIndex);
  //     } else {
  //       setOpenCreateModal(false);
  //       setStepIndex(nextStepIndex);
  //     }
  //   }
  // };
  const handleClickOpen = () => {
    setRun(stepIndex === 3 ? false : run);
    setOpenCreateModal(!openCreateModal);
    setStepIndex(stepIndex === 3 ? 4 : stepIndex);
  };
  const startTour = () => {
    setRun(true); // Turu başlatır
    setShowButton(false); // Butonu gizler
  };

  const handleModal = () => {
    setFilterModal(true);
  };

  const handleCloseModal = () => {
    setFilterModal(false);
  };
  // Service test

  function getLocations(directions) {
    const acceptGoodsID = localStorage.getItem('easypointID');
    // console.log('acceptGoods: ', acceptGoodsID);

    const filteredDirections = directions?.filter(
      (dir) => !dir.handle.includes('Easypoint')
    );

    Services.GetLocation(parsedUser.token, customer?.customer?.id)
      .then((response) => {
        if (response.data.status) {
          //console.log('lokasyonlar: ', response.data);
          setStoreLocationID(response?.data?.result[0]?.locationID);

          const permutations = [];

          for (let i = 0; i < response.data.result?.length; i++) {
            permutations.push({
              title: `Mal Kabulden → ${response.data.result[i]?.title}`,
              handle: 'MallLogisticsToStore',
              type: 'new',
              from: acceptGoodsID,
              to: response.data.result[i]?.locationID,
            });
            permutations.push({
              title: `${response.data.result[i].title} → Mal Kabule`,
              handle: 'StoreToMallLogistics',
              type: 'new',
              from: response.data.result[i].locationID,
              to: acceptGoodsID,
            });

            for (let j = 0; j < response.data.result?.length; j++) {
              if (i !== j) {
                permutations.push({
                  title: `${response.data.result[i].title} → ${response.data.result[j].title}`,
                  handle: 'StoreToStore',
                  type: 'new',
                  from: response.data.result[i].locationID,
                  to: response.data.result[j].locationID,
                });
              }
            }
          }

          directions = filteredDirections.concat(permutations);
          setLocations(directions);
          if (showCreateModal && showCreateModal === true) {
            if (isCurrentBranchBlocked()) {
              requestBlockedDialog();
            } else {
              setOpenCreateModal(true);
            }
          }
        } else {
          // toast.error(response.data.message);
          // console.log('lokasyonlar false döndü');
        }
      })
      .catch((error) => {
        // toast.error(error.message);
        // console.log("lokasyonlar catch'e girdi", error);
      });
  }

  function getDirections() {
    Services.GetDirections(parsedUser.token)
      .then((response) => {
        if (response.data.status) {
          if (tokenService?.getUser()?.accountCategory === 'store') {
            getLocations(
              response.data.result.filter((x) => {
                x.handle != 'StoreToStore' ||
                  x.title === "Mağazadan Mall Logistics'e" ||
                  x.title === "Mall Logistics'ten Mağazaya" ||
                  x.title === "Easypoint'ten Mall Logistics'e" ||
                  x.title === "Mall Logistics'ten Easypoint'e";
              })
            );
          } else {
            if (showCreateModal && showCreateModal === true) {
              if (isCurrentBranchBlocked()) {
                requestBlockedDialog();
              } else {
                setOpenCreateModal(true);
              }
            }
          }
        } else {
          // console.log('yönler false döndü');
          // toast.error(response.data.message);
        }
      })
      .catch((error) => {
        // console.log("yönler catch'e girdi", error);
        // toast.error(error.message);
      });
  }
  function getFilteredOrderList() {
    setOrderList([]);
    setIsLoading(true);
    const currentDate = moment(new Date()).format('YYYY-MM-DD');
    if (
      (customer?.customer?.id &&
        tokenService?.getUser()?.accountCategory === 'store') ||
      (tokenService?.getUser()?.accountCategory === 'company' &&
        customer?.customer?.branchID)
    ) {
      Services.GetPostsCompany(
        parsedUser.token,
        tokenService?.getUser()?.accountCategory === 'company'
          ? customer?.customer?.branchID
          : customer?.customer?.id,
        filters.selectedCargoStatus && filters.selectedCargoStatus.length > 0
          ? filters.selectedCargoStatus.map((x) => x.id)
          : StatusList.filter((x) => x.id != 0).map((x) => x.id),
        filters.startDate
          ? moment(filters.startDate).format('YYYY-MM-DD')
          : currentDate,
        filters.endDate
          ? moment(filters.endDate).format('YYYY-MM-DD')
          : currentDate,
        tokenService?.getUser()?.accountCategory === 'company' &&
          customer?.customer?.branchID
          ? true
          : false
      )
        .then((response) => {
          if (response.data.status) {
            let filteredResults = response.data.result;
            console.log(filteredResults[0]);
            if (filters.selectedShipmentType !== 'Tümü') {
              filteredResults = filteredResults.filter(
                (order) => order.apiUserType === filters.selectedShipmentType
              );
            }

            if (filters.selectedFromTitle !== 'Tümü') {
              filteredResults = filteredResults.filter(
                (order) => order.from.title === filters.selectedFromTitle
              );
            }

            if (filters.selectedToTitle !== 'Tümü') {
              filteredResults = filteredResults.filter(
                (order) => order.to.title === filters.selectedToTitle
              );
            }

            if (filters.selectedFromOrToTitle !== 'Tümü') {
              filteredResults = filteredResults.filter(
                (order) =>
                  order.to.title === filters.selectedFromOrToTitle ||
                  order.from.title === filters.selectedFromOrToTitle
              );
            }

            if (filters.selectedCount !== '') {
              filteredResults = filteredResults.filter(
                (order) => order.orderInventory === filters.selectedCount
              );
            }

            if (filters.selectedPerson !== '') {
              const lowerCaseSelectedPerson =
                filters.selectedPerson.toLowerCase();
              filteredResults = filteredResults.filter((order) => {
                const porterName =
                  order?.porterNameLastname ||
                  (order?.porters && order.porters[0]?.porterNameLastname);
                return (
                  porterName &&
                  porterName.toLowerCase().includes(lowerCaseSelectedPerson)
                );
              });
            }

            if (filters.filterMultiple) {
              filteredResults = filteredResults.filter(
                (order) =>
                  order.dataJSON?.isMultipleTransportation === true ||
                  order.dataJSON?.isMultipleTransportation === '1'
              );
            }

            if (filters.selectedDocumentNo !== '') {
              const lowerCaseSelectedDocument =
                filters.selectedDocumentNo.toLowerCase();
              filteredResults = filteredResults.filter((order) =>
                order?.documentNo
                  ?.toLowerCase()
                  ?.includes(lowerCaseSelectedDocument)
              );
            }

            if (filters.selectedHandshakeNo !== '') {
              const lowerCaseSelectedHandshake =
                filters.selectedHandshakeNo.toLowerCase();
              filteredResults = filteredResults.filter((order) =>
                order?.handshakeID
                  ?.toLowerCase()
                  ?.includes(lowerCaseSelectedHandshake)
              );
            }

            if (filters.selectedShipmentFirm !== '') {
              const lowerCaseSelectedShipmentFirm =
                filters.selectedShipmentFirm.toLowerCase();
              filteredResults = filteredResults.filter((order) =>
                order?.shipmentFirm
                  ?.toLowerCase()
                  ?.includes(lowerCaseSelectedShipmentFirm)
              );
            }

            if (filters.selectedVehicle !== 'Tümü') {
              const lowerCaseSelectedVehicle =
                filters.selectedVehicle.toLowerCase();
              const isKoliKargo = filters.selectedVehicle === 'Koli / Kargo';
              filteredResults = filteredResults.filter((order) => {
                const matches = (text) => {
                  const t = text?.toLowerCase() || '';
                  return (
                    t.includes(lowerCaseSelectedVehicle) ||
                    (isKoliKargo && t.includes('ring'))
                  );
                };

                if (order?.usedVehicles?.length > 0) {
                  return order.usedVehicles.some((e) =>
                    matches(e.transportationTool)
                  );
                } else {
                  return matches(order.requestedTransportationTool);
                }
              });
            }

            if (filters.selectedCreatorName !== '') {
              filteredResults = filteredResults.filter((order) => {
                const creatorFullName = (
                  order.createdByName +
                  ' ' +
                  order.createdByLastname
                ).toLowerCase();
                return creatorFullName.includes(
                  filters.selectedCreatorName.toLowerCase()
                );
              });
            }

            if (filters.selectedCreatorType !== 'Tümü') {
              filteredResults = filteredResults.filter((order) => {
                if (filters.selectedCreatorType === 'Belirtilmemiş') {
                  return !order.createdBy;
                } else {
                  return order.createdBy === filters.selectedCreatorType;
                }
              });
            }

            setOrderList(filteredResults);
            setOriginalOrderList(response.data.result);
            // Extract unique from.title and to.title values
            const uniqueFromTitles = [
              'Tümü',
              ...new Set(
                response.data.result
                  .map((order) => order.from.title)
                  .filter((title) => title !== null && title !== undefined)
              ),
            ];
            const uniqueToTitles = [
              'Tümü',
              ...new Set(
                response.data.result
                  .map((order) => order.to.title)
                  .filter((title) => title !== null && title !== undefined)
              ),
            ];

            setFromTitles(uniqueFromTitles);
            setToTitles(uniqueToTitles);
          } else {
            setOrderList([]);
            setOriginalOrderList([]);
            toast.error('İş emri bulunamadı.');
          }
          setIsLoading(false);
        })
        .catch((error) => {
          setOrderList([]);
          setOriginalOrderList([]);
          toast.error('İş emri bulunamadı.');
          setIsLoading(false);
        });
    } else if (
      (tokenService?.getUser()?.accountCategory === 'mall-logistics' ||
        tokenService?.getUser()?.accountCategory === 'mall') &&
      customer?.customer?.id !== undefined
    ) {
      Services.GetPostsMall(
        parsedUser.token,
        customer?.customer?.id,
        filters.selectedCargoStatus && filters.selectedCargoStatus.length > 0
          ? filters.selectedCargoStatus.map((x) => x.id)
          : StatusList.filter((x) => x.id != 0).map((x) => x.id),
        filters.startDate
          ? moment(filters.startDate).format('DD-MM-YYYY')
          : currentDate,
        filters.endDate
          ? moment(filters.endDate).format('DD-MM-YYYY')
          : currentDate
      )
        .then((response) => {
          if (response.data.status) {
            let filteredResults = response.data.result;

            if (filters.selectedShipmentType !== 'Tümü') {
              filteredResults = filteredResults.filter(
                (order) => order.apiUserType === filters.selectedShipmentType
              );
            }

            if (filters.selectedFromTitle !== 'Tümü') {
              filteredResults = filteredResults.filter(
                (order) => order.from.title === filters.selectedFromTitle
              );
            }

            if (filters.selectedToTitle !== 'Tümü') {
              filteredResults = filteredResults.filter(
                (order) => order.to.title === filters.selectedToTitle
              );
            }

            if (filters.selectedFromOrToTitle !== 'Tümü') {
              filteredResults = filteredResults.filter(
                (order) =>
                  order.to.title === filters.selectedFromOrToTitle ||
                  order.from.title === filters.selectedFromOrToTitle
              );
            }

            if (filters.selectedCount !== '') {
              filteredResults = filteredResults.filter(
                (order) => order.orderInventory === filters.selectedCount
              );
            }

            if (filters.selectedPerson !== '') {
              const lowerCaseSelectedPerson =
                filters.selectedPerson.toLowerCase();
              filteredResults = filteredResults.filter((order) => {
                const porterName =
                  order?.porterNameLastname ||
                  (order?.porters && order.porters[0]?.porterNameLastname);
                return (
                  porterName &&
                  porterName.toLowerCase().includes(lowerCaseSelectedPerson)
                );
              });
            }

            if (filters.filterMultiple) {
              filteredResults = filteredResults.filter(
                (order) =>
                  order.dataJSON?.isMultipleTransportation === true ||
                  order.dataJSON?.isMultipleTransportation === '1'
              );
            }

            if (filters.selectedDocumentNo !== '') {
              const lowerCaseSelectedDocument =
                filters.selectedDocumentNo.toLowerCase();
              filteredResults = filteredResults.filter((order) =>
                order?.documentNo
                  ?.toLowerCase()
                  ?.includes(lowerCaseSelectedDocument)
              );
            }

            if (filters.selectedHandshakeNo !== '') {
              const lowerCaseSelectedHandshake =
                filters.selectedHandshakeNo.toLowerCase();
              filteredResults = filteredResults.filter((order) =>
                order?.handshakeID
                  ?.toLowerCase()
                  ?.includes(lowerCaseSelectedHandshake)
              );
            }

            if (filters.selectedShipmentFirm !== '') {
              const lowerCaseSelectedShipmentFirm =
                filters.selectedShipmentFirm.toLowerCase();
              filteredResults = filteredResults.filter((order) =>
                order?.shipmentFirm
                  ?.toLowerCase()
                  ?.includes(lowerCaseSelectedShipmentFirm)
              );
            }

            if (filters.selectedVehicle !== 'Tümü') {
              const lowerCaseSelectedVehicle =
                filters.selectedVehicle.toLowerCase();
              const isKoliKargo = filters.selectedVehicle === 'Koli / Kargo';
              filteredResults = filteredResults.filter((order) => {
                const matches = (text) => {
                  const t = text?.toLowerCase() || '';
                  return (
                    t.includes(lowerCaseSelectedVehicle) ||
                    (isKoliKargo && t.includes('ring'))
                  );
                };

                if (order?.usedVehicles?.length > 0) {
                  return order.usedVehicles.some((e) =>
                    matches(e.transportationTool)
                  );
                } else {
                  return matches(order.requestedTransportationTool);
                }
              });
            }

            if (filters.selectedCreatorName !== '') {
              filteredResults = filteredResults.filter((order) => {
                const creatorFullName = (
                  order.createdByName +
                  ' ' +
                  order.createdByLastname
                ).toLowerCase();
                return creatorFullName.includes(
                  filters.selectedCreatorName.toLowerCase()
                );
              });
            }

            if (filters.selectedCreatorType !== 'Tümü') {
              filteredResults = filteredResults.filter((order) => {
                if (filters.selectedCreatorType === 'Belirtilmemiş') {
                  return !order.createdBy;
                } else {
                  return order.createdBy === filters.selectedCreatorType;
                }
              });
            }
            setOrderList(filteredResults);
            setOriginalOrderList(response.data.result);

            const uniqueFromTitles = [
              'Tümü',
              ...new Set(
                response.data.result
                  .map((order) => order.from.title)
                  .filter((title) => title !== null && title !== undefined)
              ),
            ];
            const uniqueToTitles = [
              'Tümü',
              ...new Set(
                response.data.result
                  .map((order) => order.to.title)
                  .filter((title) => title !== null && title !== undefined)
              ),
            ];

            setFromTitles(uniqueFromTitles);
            setToTitles(uniqueToTitles);
          } else {
            setOrderList([]);
            setOriginalOrderList([]);
            toast.error('İş emri bulunamadı.');
          }
          setIsLoading(false);
        })
        .catch((error) => {
          setOrderList([]);
          setOriginalOrderList([]);
          toast.error('İş emri bulunamadı.');
          setIsLoading(false);
        });
    }
  }

  function exportToExcel() {
    let temp1 = orderList.map((x, index) => {
      let usedVehiclesText = '';
      if (x?.usedVehicles && x?.usedVehicles.length > 0) {
        usedVehiclesText = x.usedVehicles
          .map(
            (vehicle) =>
              `${
                vehicle.transportationTool === 'Ring'
                  ? 'Koli/Paket (Ring)'
                  : vehicle.transportationTool
              } (${vehicle.transportationToolCount})`
          )
          .join(', ');
      } else if (x?.requestedTransportationTool) {
        usedVehiclesText = `${
          x.requestedTransportationTool === 'Ring'
            ? 'Koli/Paket (Ring)'
            : x.requestedTransportationTool
        } (1)`;
      }

      let createdByInfo = '';
      let type = '';

      if (!x.createdBy) {
        type = 'Belirtilmemiş';
      } else {
        if (x.createdBy === 'corporational') {
          type = 'Mağaza';
        } else if (x.createdBy === 'porter') {
          type = 'Porter';
        } else {
          type = x.createdBy;
        }
      }

      createdByInfo =
        (x.createdByName ? x.createdByName : '-') +
        (x.createdByLastname ? x.createdByLastname : '-') +
        ' / ' +
        type.charAt(0).toUpperCase() +
        type.slice(1);

      // Porter taşıma detaylarını oluşturuyoruz:
      let porterTransportationDetails = '';
      let porterNameInfo = '';
      const porterAgg = buildPorterAggregatesFromUsedVehicles(
        x?.usedVehicles || []
      );
      if (porterAgg.length > 0) {
        porterNameInfo = porterAgg.map((p) => p.porterNameLastname).join(', ');
        porterTransportationDetails = porterAgg
          .map((p) => {
            const toolsDetails = Object.entries(p.transportationTools)
              .map(
                ([tool, count]) =>
                  `${count} sefer ${
                    tool === 'Ring' ? 'Koli/Paket (Ring)' : tool
                  }`
              )
              .join(', ');
            return `${p.porterNameLastname}: ${toolsDetails}`;
          })
          .join(' | ');
      }
      let processTime = '';
      if (x.completedDateTime) {
        processTime = calculateAndFormatDeliveryTime(
          x.createdDateTime,
          x.completedDateTime
        );
      }
      // ---------------------------------------------------

      let a = {
        id: index + 1,
        createdDateTime: moment(x.createdDateTime * 1000).format('DD/MM/YYYY'),
        createdTime: moment(x.createdDateTime * 1000).format('HH:mm'),
        barcode: x.barcode,
        documentNo: x.documentNo ? x.documentNo : '',
        handshakeID: x.handshakeID ? x.handshakeID : '',
        title: x.title.split(' - ')[0] + ' - ' + x.title.split(' - ')[1],
        from: x.from.title,
        to: x.to.title ? x.to.title : '',
        orderInventory: +x?.orderInventory,
        usedVehicles: usedVehiclesText,
        completedDateTime: x.completedDateTime
          ? moment(new Date(Number(x.completedDateTime * 1000))).format(
              'DD/MM/YYYY'
            )
          : '',
        completedTime: x.completedDateTime
          ? moment(new Date(Number(x.completedDateTime * 1000))).format('HH:mm')
          : '',
        processTime: processTime, //
        shipmentFirm: x.shipmentFirm ? x.shipmentFirm : '',
        createdByInfo: createdByInfo,
        porterNameLastname: porterNameInfo,
        porterTransportationDetails: porterTransportationDetails,
        status: StatusList.filter((item) => item.id.toString() === x.status)[0]
          ?.label,
        cancelReason: x.cancelReason ? x.cancelReason : '',
      };
      return a;
    });

    api
      .post('excel/export', {
        type: 'excel',
        content: temp1,
        header: {
          No: 'number',
          'Oluşturulma Tarihi': 'string',
          'Oluşturulma Saati': 'string',
          'Kargo Takip No': 'string',
          'Belge No': 'string',
          'İrsaliye No': 'string',
          'Firma/Kişi': 'string',
          'Çıkış Noktası': 'string',
          'Varış Noktası': 'string',
          'Adet Bilgisi': 'number',
          'Taşıma Bilgisi': 'string',
          'Tamamlanma Tarihi': 'string',
          'Tamamlanma Saati': 'string',
          'Taşıma Süresi': 'string',
          'Kargo Firması': 'string',
          'Oluşturan Kişi Adı / Türü': 'string',
          'Taşıyan Kişi': 'string',
          'Porter Taşıma Detayları': 'string',
          'İşlem Durumu': 'string',
          'İptal Nedeni': 'string',
        },
        sheetTitle: 'İşler',
      })
      .then((response) => {
        if (response.data.status) {
          try {
            const newWindow = window.open(response.data.result, '_blank');
            if (
              !newWindow ||
              newWindow.closed ||
              typeof newWindow.closed == 'undefined'
            ) {
              setShowPopUpError(true);
              throw new Error(
                'Pop-up engellendi. Lütfen pop-up engelleyicisini devre dışı bırakın.'
              );
            }
          } catch (e) {
            setShowPopUpError(true);
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  }

  function getBranches() {
    const mallID = localStorage.getItem('mallID');
    Services.GetStores(parsedUser.token, mallID)
      .then((res) => {
        if (res.data.status) {
          setBranches(
            res.data.result.map((branch) => ({
              branchID: branch.branchID,
              title: branch.title,
              locations: branch.locations,
            }))
          );
        }
      })
      .catch((err) => {
        // console.log("get branches catch'e girdi: ", err);
        setBranches([]);
      });
  }

  useEffect(() => {
    if (customer) {
      // console.log('customer: ', customer);
      getFilteredOrderList();
      getDirections();
      if (tokenService?.getUser()?.accountCategory === 'mall-logistics') {
        getBranches();
      }
    }
  }, [customer]);

  useEffect(() => {
    const mall = localStorage.getItem('mallID');
    api
      .post('flow/malls-get-default-transportation-tools', {
        mallID: mall,
      })
      .then((res) => {
        if (res.data.status) {
          setVehicles(res.data.result);
        } else {
          setVehicles([]);
          toast.error('İş oluşturmada bir hata meydana geldi.');
        }
      })
      .catch((err) => {
        // console.log(err);
        setVehicles([]);
        toast.error('İş oluşturmada bir hata meydana geldi.');
      });
  }, []);

  const handleSearchByBarcode = (barcode) => {
    setOrderList([]);
    if (!barcode) {
      setOrderList(originalOrderList);
    } else {
      const filteredOrders = originalOrderList.filter((order) =>
        order.barcode.includes(barcode)
      );
      setOrderList(filteredOrders);
    }
  };

  const clearFilters = () => {
    clearFiltersCalledRef.current = true;
    setFilters(initialFilters);
    setSearchParams({});
  };

  useEffect(() => {
    // Filtreler güncellendiyse ve clearFilters tetiklenmişse
    if (
      clearFiltersCalledRef.current &&
      JSON.stringify(filters) === JSON.stringify(initialFilters)
    ) {
      getFilteredOrderList();
      clearFiltersCalledRef.current = false; // reset
    }
  }, [filters]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setFilters({ ...filters, selectedCargoStatus: value });
  };

  const handleShipmentTypeChange = (event) => {
    setFilters({ ...filters, selectedShipmentType: event.target.value });
  };

  const asiMetFunct = () => {
    if (isCurrentBranchBlocked()) {
      requestBlockedDialog();
      return;
    }
    setOpenCreateModal(true);
  };

  const firstInputRef = useRef(null);

  useEffect(() => {
    if (filterModal && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [filterModal]);

  const creatorTypeLabels = {
    Tümü: 'Oluşturan Türü',
    Belirtilmemiş: 'Belirtilmemiş',
    corporational: 'Mağaza',
    'eren-holding': 'Eren Holding',
    getir: 'Getir',
    highadmin: 'Highadmin',
    'low-admin': 'Lowadmin',
    muhaberat: 'Muhaberat',
    porter: 'Porter',
    derimod: 'Derimod',
    ipekyol: 'İpekyol',
  };

  return (
    <>
      <Helmet>
        <title> {language.finished_jobs.titles.site_title} </title>
      </Helmet>
      <Backdrop sx={{ color: '#fff', zIndex: 2 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Stack style={{ paddingInline: '25px' }} className="my-zero-step">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Stack direction="row">
            <Typography variant="h4" gutterBottom margin={0}>
              {language.finished_jobs.titles.work_orders}
            </Typography>
          </Stack>

          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            alignItems="end"
          >
            {tokenService.getUser()?.accountCategory !== 'mall' &&
              tokenService.getUser()?.frontAuth.includes('operations') && (
                <Button
                  className="my-first-step"
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  onClick={() => asiMetFunct()}
                >
                  {language.finished_jobs.buttons.create_workOrder}
                </Button>
              )}
            <ExportToExcelButton
              setShowPopUpError={setShowPopUpError}
              showPopUpError={showPopUpError}
              sx={{ marginLeft: '7px' }}
              onClick={() => exportToExcel()}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent={'end'}></Stack>
        <Stack
          direction="row"
          justifyContent={isMobile ? 'space-between' : 'end'}
          mb={5}
        >
          {isMobile ? (
            <FilterViewButton variant="contained" onClick={handleModal} />
          ) : null}
        </Stack>

        <Card>
          <Dialog
            open={selectedJob}
            onClose={() => setSelectedJob(null)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {language.finished_jobs.buttons.multiple_tools_type}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {/* Kullanılan araçlar */}
                <Typography variant="subtitle1" gutterBottom>
                  Taşınan Araçlar
                </Typography>
                {selectedJob?.usedVehicles.map((e, index) => (
                  <div key={index}>
                    {e.transportationToolCount} sefer{' '}
                    {e.transportationTool === 'Ring'
                      ? 'Koli/Paket (Ring)'
                      : e.transportationTool}
                  </div>
                ))}

                {/* Porter taşıma detayları */}
                {selectedJob?.usedVehicles &&
                  selectedJob.usedVehicles.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Porter Taşıma Detayları
                      </Typography>
                      {buildPorterAggregatesFromUsedVehicles(
                        selectedJob.usedVehicles
                      ).map((porter) => (
                        <div
                          key={porter.porterID}
                          style={{ marginBottom: '8px' }}
                        >
                          <strong>{porter.porterNameLastname}</strong>:
                          {Object.entries(porter.transportationTools).map(
                            ([tool, count]) => (
                              <div key={tool}>
                                {count} sefer{' '}
                                {tool === 'Ring' ? 'Koli/Paket (Ring)' : tool}
                              </div>
                            )
                          )}
                        </div>
                      ))}
                    </div>
                  )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedJob(null)}>
                {language.commons.close}
              </Button>
            </DialogActions>
          </Dialog>

          <CustomFilter
            modalType={'drawer'}
            filterModal={filterModal}
            handleCloseModal={handleCloseModal}
          >
            <>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="space-between"
                paddingY={2}
                paddingX={1}
                spacing={2}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.filterMultiple}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          filterMultiple: e.target.checked,
                        })
                      }
                      color="primary"
                    />
                  }
                  label="Çoklu Gönderim"
                  sx={{ width: '100%' }}
                />

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <Select
                    multiple
                    displayEmpty
                    value={filters.selectedCargoStatus}
                    input={<OutlinedInput ref={firstInputRef} autoFocus />}
                    onChange={handleChange}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <em> {language.commons.cargo_status}</em>;
                      }
                      return selected.map((x) => x.label).join(', ');
                    }}
                    MenuProps={MenuProps}
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    {StatusList.sort((a, b) =>
                      a.label.localeCompare(b.label)
                    ).map((x) => (
                      <MenuItem key={x.id} value={x}>
                        {x.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <TextField
                    sx={{
                      flex: 1.5,
                      '& .MuiInputLabel-root': { color: '#212B36' },
                    }}
                    label="Oluşturan Kişi Adı"
                    variant="outlined"
                    value={filters.selectedCreatorName}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        selectedCreatorName: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <Select
                    displayEmpty
                    value={filters.selectedCreatorType}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        selectedCreatorType: e.target.value,
                      })
                    }
                    renderValue={(selected) => {
                      if (selected === 'Tümü') {
                        return <em>{creatorTypeLabels[selected]}</em>;
                      }
                      return creatorTypeLabels[selected] || selected;
                    }}
                    input={<OutlinedInput />}
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="Tümü">
                      <em>{creatorTypeLabels['Tümü']}</em>
                    </MenuItem>
                    <MenuItem value="Belirtilmemiş">
                      {creatorTypeLabels['Belirtilmemiş']}
                    </MenuItem>
                    <MenuItem value="corporational">
                      {creatorTypeLabels['corporational']}
                    </MenuItem>
                    <MenuItem value="eren-holding">
                      {creatorTypeLabels['eren-holding']}
                    </MenuItem>
                    <MenuItem value="getir">
                      {creatorTypeLabels['getir']}
                    </MenuItem>
                    <MenuItem value="highadmin">
                      {creatorTypeLabels['highadmin']}
                    </MenuItem>
                    <MenuItem value="low-admin">
                      {creatorTypeLabels['low-admin']}
                    </MenuItem>
                    <MenuItem value="muhaberat">
                      {creatorTypeLabels['muhaberat']}
                    </MenuItem>
                    <MenuItem value="porter">
                      {creatorTypeLabels['porter']}
                    </MenuItem>
                    <MenuItem value="derimod">
                      {creatorTypeLabels['derimod']}
                    </MenuItem>
                    <MenuItem value="ipekyol">
                      {creatorTypeLabels['ipekyol']}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <TextField
                    sx={{
                      flex: 1.5,
                      '& .MuiInputLabel-root': {
                        color: '#212B36',
                      },
                    }}
                    label={language.commons.person_carrying}
                    variant="outlined"
                    value={filters.selectedPerson}
                    onChange={(e) =>
                      setFilters({ ...filters, selectedPerson: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <TextField
                    sx={{
                      flex: 1.5,
                      '& .MuiInputLabel-root': {
                        color: '#212B36',
                      },
                    }}
                    label={'Belge No'}
                    variant="outlined"
                    value={filters.selectedDocumentNo}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        selectedDocumentNo: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <TextField
                    sx={{
                      flex: 1.5,
                      '& .MuiInputLabel-root': {
                        color: '#212B36',
                      },
                    }}
                    label={'İrsaliye No'}
                    variant="outlined"
                    value={filters.selectedHandshakeNo}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        selectedHandshakeNo: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <TextField
                    sx={{
                      flex: 1.5,
                      '& .MuiInputLabel-root': {
                        color: '#212B36',
                      },
                    }}
                    label={'Kargo Firması'}
                    variant="outlined"
                    value={filters.selectedShipmentFirm}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        selectedShipmentFirm: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <TextField
                    sx={{
                      flex: 1.5,
                      '& .MuiInputLabel-root': {
                        color: '#212B36',
                      },
                    }}
                    label={language.commons.quantity_info}
                    variant="outlined"
                    value={filters.selectedCount}
                    onChange={(e) =>
                      setFilters({ ...filters, selectedCount: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <Select
                    value={filters.selectedVehicle}
                    input={<OutlinedInput />}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        selectedVehicle: e.target.value,
                      })
                    }
                    renderValue={(selected) => {
                      if (selected === 'Tümü') {
                        return <em>{language.commons.vehicle_type}</em>;
                      }
                      return selected;
                    }}
                    MenuProps={MenuProps}
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    {cargoList.map((x, index) => (
                      <MenuItem
                        key={index}
                        value={x === 'Koli / Paket' ? 'Ring' : x}
                      >
                        {x}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <Select
                    displayEmpty
                    value={filters.selectedShipmentType}
                    input={<OutlinedInput />}
                    onChange={handleShipmentTypeChange}
                    renderValue={(selected) => {
                      if (selected === 'Tümü') {
                        return <em>{language.commons.shipment_type}</em>;
                      }
                      return selected;
                    }}
                    MenuProps={MenuProps}
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    <MenuItem value="Tümü">
                      <em>Tümü</em>
                    </MenuItem>
                    <MenuItem value="getir">
                      {language.commons.brands.getir}
                    </MenuItem>
                    <MenuItem value="handsfree">
                      {language.commons.brands.handsfree}
                    </MenuItem>
                    <MenuItem value="hepsiburada">
                      {language.commons.brands.hepsiburada}
                    </MenuItem>
                    <MenuItem value="mall-logistics">
                      {language.commons.brands.mallLogistics}
                    </MenuItem>
                    <MenuItem value="trendyol">
                      {language.commons.brands.trendyol}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <Select
                    displayEmpty
                    value={filters.selectedFromTitle}
                    input={<OutlinedInput />}
                    onChange={(event) =>
                      setFilters({
                        ...filters,
                        selectedFromTitle: event.target.value,
                      })
                    }
                    renderValue={(selected) => {
                      if (selected === 'Tümü') {
                        return <em>{language.commons.exit_point}</em>;
                      }
                      return selected;
                    }}
                    MenuProps={MenuProps}
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    {fromTitles
                      .sort((a, b) => a.localeCompare(b))
                      .map((title) => (
                        <MenuItem key={title} value={title}>
                          {title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <Select
                    displayEmpty
                    value={filters.selectedToTitle}
                    input={<OutlinedInput />}
                    onChange={(event) =>
                      setFilters({
                        ...filters,
                        selectedToTitle: event.target.value,
                      })
                    }
                    renderValue={(selected) => {
                      if (selected === 'Tümü') {
                        return <em>{language.commons.destination_point}</em>;
                      }
                      return selected;
                    }}
                    MenuProps={MenuProps}
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    {toTitles
                      .sort((a, b) => a.localeCompare(b))
                      .map((title) => (
                        <MenuItem key={title} value={title}>
                          {title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                  <Select
                    displayEmpty
                    value={filters.selectedFromOrToTitle}
                    input={<OutlinedInput />}
                    onChange={(event) =>
                      setFilters({
                        ...filters,
                        selectedFromOrToTitle: event.target.value,
                      })
                    }
                    renderValue={(selected) => {
                      if (selected === 'Tümü') {
                        return <em>Çıkış / Varış Noktası</em>;
                      }
                      return selected;
                    }}
                    MenuProps={MenuProps}
                    inputProps={{ 'aria-label': 'Without label' }}
                  >
                    {Array.from(new Set([...toTitles, ...fromTitles]))
                      .sort((a, b) => a.localeCompare(b))
                      .map((title) => (
                        <MenuItem key={title} value={title}>
                          {title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Stack>

              <Stack
                direction="column"
                alignItems="baseline"
                justifyContent="space-between"
                paddingY={2}
                paddingX={1}
                spacing={2}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  spacing={2}
                  sx={{ flex: 1 }}
                >
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      sx={{ flex: 1 }}
                      format="DD/MM/YYYY"
                      label={language.commons.start_date}
                      value={filters.startDate}
                      onChange={(newValue) =>
                        setFilters({ ...filters, startDate: newValue })
                      }
                    />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      sx={{ flex: 1 }}
                      format="DD/MM/YYYY"
                      label={language.commons.end_date}
                      value={filters.endDate}
                      onChange={(newValue) =>
                        setFilters({ ...filters, endDate: newValue })
                      }
                    />
                  </LocalizationProvider>
                </Stack>

                <div style={{ flex: 0.2 }}></div>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ flex: 1 }}
                  width={'100%'}
                >
                  <TextField
                    sx={{ flex: 1.5 }}
                    label="Barkod"
                    variant="outlined"
                    value={filters.barcode}
                    onChange={(e) =>
                      setFilters({ ...filters, barcode: e.target.value.trim() })
                    }
                  />
                  <Button
                    sx={{
                      backgroundColor: '#2C7EF8',
                      fontWeight: '500',
                      flexShrink: 1,
                    }}
                    variant="contained"
                    onClick={() => handleSearchByBarcode(filters.barcode)}
                  >
                    {language.commons.search}
                  </Button>
                </Stack>
              </Stack>
              <Divider sx={{ width: '100%' }} />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                paddingY={2}
                paddingX={1}
              >
                <ClearButton onClick={() => clearFilters()} />
                <FilterButton onClick={() => getFilteredOrderList()} />
              </Stack>
            </>
          </CustomFilter>

          <DynamicTable
            data={orderList}
            headers={TABLE_HEAD}
            initialOrder={'createdDateTime'}
            initalOrderDirection={'desc'}
            isOrdered={true}
            isMobile={isMobile}
            handleModal={handleModal}
            handleCloseModal={handleCloseModal}
            rowOnClick={(row) =>
              window.open(
                `/dashboard/finished-jobs-detail/${encodeURIComponent(
                  row.barcode
                )}`,
                '_blank'
              )
            }
          />
        </Card>
      </Stack>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={() => setOpen(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          {language.commons.edit}
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          {language.commons.delete}
        </MenuItem>
      </Popover>

      {openCreateModal && (
        <CreateWorkOrderModal
          handleClickOpen={handleClickOpen}
          openCreateModal={openCreateModal}
          onCreateModal={() => setOpenCreateModal(false)}
          customer={customer}
          locations={locations}
          // storeLocationID={storeLocationID}
          branches={branches}
        />
      )}
    </>
  );
}
